import { useEffect, useState } from "react";

import { Tracing } from "../../../App.tracing";
import { sendEvent } from "../../Base/Utilities/SendEvent";

function getUuidV1TimeParts(eventId) {
  const [timeLow, timeMid, timeHighAndVersion] = eventId?.split("-") ?? [];
  if (!timeLow || !timeMid || !timeHighAndVersion || timeHighAndVersion[0] !== "1") {
    return null;
  }

  // UUID v1 stores its timestamp across three fields; compare them from most to least significant.
  const timeParts = [
    parseInt(timeHighAndVersion, 16),
    parseInt(timeMid, 16),
    parseInt(timeLow, 16),
  ];
  if (!timeParts.every(Number.isFinite)) {
    return null;
  }

  timeParts[0] &= 0x0fff;
  return timeParts;
}

function sortByCreationOrder(events) {
  const eventsWithTime = events.map((event, index) => ({ event, index, timeParts: getUuidV1TimeParts(event.eventId) }));
  if (eventsWithTime.some(({ timeParts }) => !timeParts)) {
    return null;
  }

  return eventsWithTime
    .sort((first, second) => {
      for (let index = 0; index < first.timeParts.length; index += 1) {
        if (first.timeParts[index] !== second.timeParts[index]) {
          return first.timeParts[index] - second.timeParts[index];
        }
      }
      return first.index - second.index;
    })
    .map(({ event }) => event);
}

export function getPairedDefenceEvent(plays, attemptEvent) {
  const attempts = sortByCreationOrder(
    plays.filter(
      (event) =>
        event.status !== "deleted" &&
        event.eventType === "shootOutAttempt" &&
        event.entityId === attemptEvent.entityId &&
        ["made", "missed"].includes(event.subType),
    ),
  );
  const defences = sortByCreationOrder(
    plays.filter(
      (event) =>
        event.status !== "deleted" &&
        event.eventType === "shootOutDefence" &&
        event.entityId !== attemptEvent.entityId,
    ),
  );

  if (!attempts || !defences || attempts.length !== defences.length) {
    return null;
  }

  const attemptIndex = attempts.findIndex((event) => event.eventId === attemptEvent.eventId);
  return attemptIndex >= 0 ? defences[attemptIndex] ?? null : null;
}

export function useShootoutAttemptEditing(props) {
  const { currentState, eventStore } = props;
  const [attemptEditor, setAttemptEditor] = useState(null);
  const [savingAttempts, setSavingAttempts] = useState({});

  useEffect(() => {
    setSavingAttempts((currentSavingAttempts) => {
      const completedAttemptIds = Object.entries(currentSavingAttempts)
        .filter(([, eventIds]) =>
          eventIds.every((eventId) => {
            const event = eventStore?.find((storedEvent) => storedEvent.eventId === eventId);
            return event && !event.pending;
          }),
        )
        .map(([attemptEventId]) => attemptEventId);

      if (!completedAttemptIds.length) {
        return currentSavingAttempts;
      }

      const nextSavingAttempts = { ...currentSavingAttempts };
      completedAttemptIds.forEach((attemptEventId) => delete nextSavingAttempts[attemptEventId]);
      return nextSavingAttempts;
    });
  }, [currentState.updateLog, eventStore]);

  const openEditor = (event) => {
    setAttemptEditor({
      originalEvent: event,
      personId: event.personId,
      isScored: event.success,
    });
  };

  const closeEditor = () => {
    setAttemptEditor(null);
  };

  const updatePlayer = (personId) => {
    setAttemptEditor((currentEditor) => ({ ...currentEditor, personId }));
  };

  const updateOutcome = (isScored) => {
    setAttemptEditor((currentEditor) => ({ ...currentEditor, isScored }));
  };

  const pairedDefenceEvent = attemptEditor
    ? getPairedDefenceEvent(currentState.mqtt?.plays ?? [], attemptEditor.originalEvent)
    : null;

  const saveAttempt = async () => {
    const { originalEvent, personId, isScored } = attemptEditor;
    const attemptEventId = originalEvent.eventId;
    if (savingAttempts[attemptEventId]) {
      return;
    }

    const outcomeChanged = isScored !== originalEvent.success;
    if (outcomeChanged && !pairedDefenceEvent) {
      return;
    }

    const eventsToUpdate = [
      {
        ...originalEvent,
        personId,
        subType: isScored ? "made" : "missed",
        success: isScored,
      },
    ];

    if (outcomeChanged) {
      eventsToUpdate.push({
        ...pairedDefenceEvent,
        subType: isScored ? "conceded" : "saved",
      });
    }

    setSavingAttempts((currentSavingAttempts) => ({
      ...currentSavingAttempts,
      [attemptEventId]: eventsToUpdate.map((event) => event.eventId),
    }));
    closeEditor();

    try {
      await Promise.all(
        eventsToUpdate.map((event) =>
          sendEvent(
            {
              action: "sendEvent",
              value: "edit",
              type: "custom",
              event,
            },
            props,
          ),
        ),
      );
    } catch (error) {
      Tracing.capture(error);
      console.error("Unable to save the edited shootout attempt", error);
    }
  };

  return {
    attemptEditor,
    closeEditor,
    canEditOutcome: !!pairedDefenceEvent,
    hasChanges:
      !!attemptEditor &&
      (attemptEditor.personId !== attemptEditor.originalEvent.personId ||
        attemptEditor.isScored !== attemptEditor.originalEvent.success),
    isSaving: (eventId) => !!savingAttempts[eventId],
    openEditor,
    saveAttempt,
    updateOutcome,
    updatePlayer,
  };
}
